import React from "react";
import Layout from "../Layouts/layout";
import "./orderKit.scss";
import { useNavigate } from "react-router-dom";
import { fetchData, SubmitData } from "../../../services/apiService";
import {
  decryptedData,
  encryptData,
} from "../../../services/encryptionDecription.service";
import useAlertUtils, {
  validateUSPhoneNumber,
} from "../../../utils/common.utils";

function KitCheckout() {
  const [items, setItems] = React.useState([]);
  const [orderType, setOrderType] = React.useState("user");
  const navigate = useNavigate();
  const [showPayModal, setShowPayModal] = React.useState(false);
  const paypalRef = React.useRef(null);
  const shippingMethods = React.useMemo(
    () => [
      { name: "UPS Ground", code: "UPS_GROUND", price: 0 },
      { name: "UPS Two Day", code: "UPS_2ND_DAY_AIR", price: 15 },
      {
        name: "UPS Int'l Standard (Canada/Mexico only)",
        code: "UPS_STANDARD",
        price: 35,
      },
      {
        name: "UPS Int'l Expedited",
        code: "UPS_WORLDWIDE_EXPEDITED",
        price: 35,
      },
      { name: "UPS Next Afternoon", code: "UPS_NEXT_DAY_AIR", price: 35 },
      {
        name: "Priority Mail (2-3 days)",
        code: "USPS_PRIORITY_MAIL",
        price: 0,
      },
    ],
    []
  );
  const [shippingMethod, setShippingMethod] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState("");
  const [showNewUserForm, setShowNewUserForm] = React.useState(false);
  const [users, setUsers] = React.useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(false);
  const [userSearch, setUserSearch] = React.useState("");
  const [visibleCount, setVisibleCount] = React.useState(10);
  const [isAddingUser, setIsAddingUser] = React.useState(false);
  const [isFinalizingPayment, setIsFinalizingPayment] = React.useState(false);
  const [newUser, setNewUser] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [personalInfo, setPersonalInfo] = React.useState(null);
  const [isLoadingPersonal, setIsLoadingPersonal] = React.useState(false);
  const [shipTo, setShipTo] = React.useState({
    attentionTo: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    region: "",
    postalCode: "",
    countryCode: "USA",
    contactPhoneNumber: "",
    trackingEmail: "",
    isResidence: true,
  });

  const { alertSuccess, alertError } = useAlertUtils();
  React.useEffect(() => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    setItems(cart);
  }, []);

  const getProviderAccountIdFromToken = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const tokenData = JSON.parse(atob(token.split(".")[1]));
      return tokenData.userId || tokenData.id || null;
    } catch (e) {
      return null;
    }
  };

  const loadAllUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const providerId = getProviderAccountIdFromToken();
      const response = await fetchData(
        `/api/users/get-all-users/${providerId}`
      );
      const list = response?.data || [];
      setUsers(list);
      setVisibleCount(10);
      return list;
    } catch (e) {
      setUsers([]);
      return [];
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const searchUsers = async () => {
    const q = (userSearch || "").trim();
    if (!q) {
      loadAllUsers();
      return;
    }
    setIsLoadingUsers(true);
    try {
      const providerId = getProviderAccountIdFromToken();
      const response = await fetchData(
        `/api/users/search-users/${providerId}/${encodeURIComponent(q)}`
      );
      setUsers(response?.data || []);
      setVisibleCount(10);
    } catch (e) {
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  React.useEffect(() => {
    loadAllUsers();
  }, []);

  React.useEffect(() => {}, []);

  React.useEffect(() => {
    if (orderType !== "provider" || !selectedUser) return;
    if (selectedUser) {
      const selectedUserData = users.find((u) => u.id === selectedUser);
      const firstName = selectedUserData
        ? decryptedData(selectedUserData.firstName) || ""
        : "";
      const lastName = selectedUserData
        ? decryptedData(selectedUserData.lastName) || ""
        : "";
      const email = selectedUserData
        ? decryptedData(selectedUserData.email) || ""
        : "";

      const pi = personalInfo || null;
      let phone = "";
      if (pi && pi.mobileNumber) {
        const phoneCandidate = decryptedData(pi.mobileNumber) || "";
        const validation = validateUSPhoneNumber(phoneCandidate);
        if (validation.isValid) phone = phoneCandidate;
      }

      setShipTo((prev) => ({
        ...prev,
        attentionTo: `${firstName} ${lastName}`.trim(),
        addressLine1: (pi && decryptedData(pi.address1)) || "",
        addressLine2: (pi && decryptedData(pi.address2)) || "",
        addressLine3: "",
        city: (pi && decryptedData(pi.city)) || "",
        region: (pi && decryptedData(pi.state)) || "",
        postalCode: (pi && decryptedData(pi.zipCode)) || "",
        countryCode: "USA",
        contactPhoneNumber: phone,
        trackingEmail: email || prev.trackingEmail || "",
        isResidence: true,
      }));
    }
  }, [orderType, selectedUser, personalInfo, users]);

  const subtotal = React.useMemo(() => {
    return items.reduce((sum, it) => sum + (it.totalPrice || 0), 0);
  }, [items]);

  const shippingCost = React.useMemo(() => {
    const selectedShippingMethod = shippingMethods.find(
      (m) => m.code === shippingMethod
    );
    return selectedShippingMethod ? selectedShippingMethod.price || 0 : 0;
  }, [shippingMethod, shippingMethods]);

  const subtotalWithShipping = React.useMemo(() => {
    return subtotal + shippingCost;
  }, [subtotal, shippingCost]);

  const providerIdMemo = React.useMemo(
    () => getProviderAccountIdFromToken(),
    []
  );
  const isProviderPayingForSelf = React.useMemo(() => {
    return !selectedUser || String(selectedUser) === String(providerIdMemo);
  }, [selectedUser, providerIdMemo]);

  const currentTotal = React.useMemo(() => {
    if (orderType === "user") {
      return subtotalWithShipping; // User pays exact price
    } else {
      return subtotalWithShipping * 0.8; // Provider gets 20% discount
    }
  }, [orderType, subtotalWithShipping]);

  const isUserPayDisabled =
    items.length === 0 || !selectedUser || !shippingMethod;
  const isProviderPayDisabled = items.length === 0 || !selectedUser || !shippingMethod;

  const onChange = (e, set) => {
    const { name, value } = e.target;
    set((prev) => ({ ...prev, [name]: value }));
  };

  const addNewUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email) {
      alertError("Please enter first name, last name and email.");
      return;
    }
    try {
      setIsAddingUser(true);
      const payload = {
        firstName: encryptData(newUser.firstName),
        lastName: encryptData(newUser.lastName),
        email: encryptData(newUser.email),
        providerId: getProviderAccountIdFromToken(),
        role: 2,
        agreeTerms: 1,
      };
      const response = await SubmitData(
        "/api/users/register-user-by-provider",
        payload
      );
      if (response?.success) {
        alertSuccess("User created successfully.");
        try {
          const list = await loadAllUsers();
          const lowerEmail = (newUser.email || "").trim().toLowerCase();
          const found = list.find((u) => {
            try {
              const ue = (decryptedData(u.email) || "").trim().toLowerCase();
              return ue === lowerEmail;
            } catch (_) {
              return false;
            }
          });
          if (found?.id) {
            setSelectedUser(found.id);
            fetchPersonalInfo(found.id);
          }
        } catch (_) {
          await loadAllUsers();
        } finally {
          setNewUser({ firstName: "", lastName: "", email: "" });
          setShowNewUserForm(false);
        }
      } else {
        alertError(response?.message || "Failed to create user.");
      }
    } catch (e) {
      alertError("Failed to create user. Please try again.");
    } finally {
      setIsAddingUser(false);
    }
  };

  const fetchPersonalInfo = async (userId) => {
    if (!userId) return;
    setIsLoadingPersonal(true);
    try {
      const response = await fetchData(
        "/api/intakeForms/pearsonalInformation?userId=" +
          btoa(encryptData(userId.toString()))
      );
      setPersonalInfo(response?.data || null);

      const selectedUserData = users.find((u) => u.id === userId);
      if (selectedUserData) {
        const firstName = decryptedData(selectedUserData.firstName) || "";
        const lastName = decryptedData(selectedUserData.lastName) || "";
        const email = decryptedData(selectedUserData.email) || "";

        setShipTo((prev) => ({
          ...prev,
          attentionTo: `${firstName} ${lastName}`.trim(),
          trackingEmail: email,
        }));
      }
    } catch (e) {
      console.error("Error fetching personal info:", e);
      setPersonalInfo(null);
      try {
        const selectedUserData = users.find((u) => u.id === userId);
        if (selectedUserData) {
          const firstName = decryptedData(selectedUserData.firstName) || "";
          const lastName = decryptedData(selectedUserData.lastName) || "";
          const email = decryptedData(selectedUserData.email) || "";
          setShipTo((prev) => ({
            ...prev,
            attentionTo: `${firstName} ${lastName}`.trim(),
            trackingEmail: email,
          }));
        }
      } catch (_) {}
    } finally {
      setIsLoadingPersonal(false);
    }
  };

  const placeOrder = async () => {
    try {
      if (!shippingMethod) {
        alertError("Please select a shipping method.");
        return;
      }
      const token = localStorage.getItem("token");
      let providerAccountId = null;

      if (token) {
        try {
          const tokenData = JSON.parse(atob(token.split(".")[1]));
          providerAccountId = tokenData.userId || tokenData.id || 5;
        } catch (e) {
          console.warn("Could not parse token, using default provider ID");
        }
      }

      let finalShipTo = { ...shipTo };

      if (orderType === "user" && selectedUser) {
        const selectedUserData = users.find((u) => u.id === selectedUser);
        if (selectedUserData) {
          const firstName = decryptedData(selectedUserData.firstName) || "";
          const lastName = decryptedData(selectedUserData.lastName) || "";
          const email = decryptedData(selectedUserData.email) || "";
          finalShipTo.attentionTo = `${firstName} ${lastName}`.trim() || null;
          finalShipTo.trackingEmail = email || null;
        }
        if (personalInfo) {
          const phoneFromAPI = decryptedData(personalInfo.mobileNumber) || null;
          let validatedPhone = null;
          if (phoneFromAPI) {
            const phoneValidation = validateUSPhoneNumber(phoneFromAPI);
            if (phoneValidation.isValid) {
              validatedPhone = phoneFromAPI;
            }
          }

          finalShipTo = {
            ...finalShipTo,
            addressLine1: decryptedData(personalInfo.address1) || "",
            addressLine2: decryptedData(personalInfo.address2) || "",
            addressLine3: "",
            city: decryptedData(personalInfo.city) || "",
            region: decryptedData(personalInfo.state) || "",
            postalCode: decryptedData(personalInfo.zipCode) || "",
            countryCode: "USA",
            contactPhoneNumber: validatedPhone || "",
            isResidence: true,
          };
          setShipTo(finalShipTo);
        }
      }

      const kitsData = items.map((item) => ({
        name: item.kitName,
        code: item.kitCode,
        panels: [{ code: item.code }],
      }));

      const isProviderFlow = orderType === "provider";
      const orderRequestBody = {
        userID: selectedUser || providerAccountId,
        providerId: providerAccountId,
        buyerAccountId: 1163719628,
        providerContactAccountId: null,
        shippingMethodCode: shippingMethod,
        attentionTo: finalShipTo.attentionTo || null,
        addressLine1: finalShipTo.addressLine1 || null,
        addressLine2: finalShipTo.addressLine2 || null,
        addressLine3: finalShipTo.addressLine3 || "",
        city: finalShipTo.city || null,
        region: finalShipTo.region || null,
        postalCode: finalShipTo.postalCode || null,
        countryCode: "USA",
        contactNo: finalShipTo.contactPhoneNumber || null,
        trackingEmail: finalShipTo.trackingEmail || null,
        isResidence: true,
        kits: JSON.stringify(kitsData),
        total: currentTotal,
        paidBy: orderType,
        ...(isProviderFlow
          ? {
              totalPrice: Number(currentTotal.toFixed(2)),
              description: "ZRT Kit Order",
            }
          : {}),
      };

      if (isProviderFlow) {
        localStorage.setItem(
          "provider_draft_order_body",
          JSON.stringify(orderRequestBody)
        );
        await startPayment();
        return;
      }

      const response = await SubmitData(
        "/api/kit-orders/order-zrt-kit",
        orderRequestBody
      );
      if (response?.success) {
        alertSuccess("Order placed successfully!");
        localStorage.removeItem("cart");
        setItems([]);
        navigate("/app/order-kit");
      } else {
        alertError(response?.message);
      }
    } catch (error) {
      alertError("Failed to place order. Please try again.");
    }
  };

  const startPayment = async () => {
    try {
      const phoneValidation = validateUSPhoneNumber(shipTo.contactPhoneNumber);
      if (!phoneValidation.isValid) {
        alertError(phoneValidation.error);
        return;
      }

      const ensurePaypalForHost = async (host) => {
        const selector = 'script[src*="paypal.com/sdk/js"]';
        const existingScripts = Array.from(document.querySelectorAll(selector));
        const hasCorrectHost = existingScripts.some((s) =>
          s.src.includes(host)
        );
        const hasAuthorize = existingScripts.some((s) =>
          s.src.includes("intent=authorize")
        );
        if (window.paypal && hasCorrectHost && hasAuthorize)
          return window.paypal;
        existingScripts.forEach(
          (s) => s.parentNode && s.parentNode.removeChild(s)
        );
        try {
          delete window.paypal;
        } catch (_) {
          window.paypal = undefined;
        }
        const clientId = process.env.REACT_APP_PAYPAL_C_CLIENT_KEY;
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = `https://${host}/sdk/js?client-id=${clientId}&intent=authorize&components=buttons&commit=true&enable-funding=card`;
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
        return window.paypal;
      };

      const providerAccountId = getProviderAccountIdFromToken();
      const finalShipTo = { ...shipTo };
      const kitsData = items.map((item) => ({
        name: item.kitName,
        code: item.kitCode,
        panels: [{ code: item.code }],
      }));
      const orderRequestBody = {
        userID: selectedUser || providerAccountId,
        providerId: providerAccountId,
        buyerAccountId: 1163719628,
        providerContactAccountId: null,
        shippingMethodCode: shippingMethod,
        attentionTo: finalShipTo.attentionTo || null,
        addressLine1: finalShipTo.addressLine1 || null,
        addressLine2: finalShipTo.addressLine2 || null,
        addressLine3: finalShipTo.addressLine3 || "",
        city: finalShipTo.city || null,
        region: finalShipTo.region || null,
        postalCode: finalShipTo.postalCode || null,
        countryCode: "USA",
        contactNo: finalShipTo.contactPhoneNumber || null,
        trackingEmail: finalShipTo.trackingEmail || null,
        isResidence: true,
        kits: JSON.stringify(kitsData),
        total: currentTotal,
        paidBy: orderType,
      };

      const paymentRes = await SubmitData(
        "/api/kit-orders/kit-payment/authorize",
        {
          totalPrice: Number(currentTotal.toFixed(2)),
          description: "ZRT Kit Order",
        }
      );
      const paypalOrderId = paymentRes?.data?.id || paymentRes?.id;
      const links = paymentRes?.data?.links || paymentRes?.links || [];
      if (!paypalOrderId) {
        alertError("Payment could not be initiated.");
        return;
      }

      localStorage.setItem(
        "provider_draft_order_body",
        JSON.stringify(orderRequestBody)
      );
      const approveLink =
        (links.find((l) => l.rel === "approve") || {}).href || "";
      const host = approveLink.includes("sandbox.paypal.com")
        ? "www.sandbox.paypal.com"
        : "www.paypal.com";
      const paypalLibHost = await ensurePaypalForHost(host);
      if (!paypalLibHost) {
        return;
      }
      setShowPayModal(true);
      setTimeout(() => {
        if (!paypalRef.current) {
          setShowPayModal(false);
          return;
        }
        try {
          paypalRef.current.innerHTML = "";

          const config = {
            createOrder: () => {
              return paypalOrderId;
            },
            onApprove: async (data, actions) => {
              try {
                setIsFinalizingPayment(true);

                const authorizeResp = await SubmitData(
                  "/api/kit-orders/kit-payment/authorize-order",
                  {
                    paypalOrderId: data.orderID,
                  }
                );

                if (!authorizeResp?.success) {
                  alertError(
                    authorizeResp?.message || "Payment authorization failed."
                  );
                  return;
                }

                const authorizationId = authorizeResp?.data?.authorizationId;

                if (!authorizationId) {
                  alertError("Authorization ID not found in PayPal response.");
                  return;
                }

                const { totalPrice, description, ...orderBody } =
                  orderRequestBody;
                const resp = await SubmitData("/api/kit-orders/order-zrt-kit", {
                  ...orderBody,
                  paypalOrderID: String(authorizationId),
                });

                if (resp?.success) {
                  alertSuccess("Payment authorized and order submitted!");
                  localStorage.removeItem("cart");
                  setItems([]);
                  setShowPayModal(false);
                  navigate("/app/order-kit");
                } else {
                  alertError(
                    resp?.message ||
                      "Failed to submit order after authorization."
                  );
                }
              } catch (err) {
                console.error("Payment authorization error:", err);
                alertError("Payment authorization failed. Please try again.");
              } finally {
                localStorage.removeItem("provider_draft_order_body");
                setIsFinalizingPayment(false);
                setShowPayModal(false);
              }
            },
            onCancel: () => {
              if (!isFinalizingPayment) setShowPayModal(false);
            },
            onError: () => {
              alertError("Payment initialization failed.");
              if (!isFinalizingPayment) setShowPayModal(false);
            },
          };
          paypalLibHost.Buttons(config).render(paypalRef.current);
        } catch (err) {
          alertError("Payment setup failed.");
          setShowPayModal(false);
        }
      }, 0);
    } catch (_) {
      alertError("Payment setup failed.");
    }
  };

  return (
    <>
      <Layout>
        <div className="checkoutPage">
          <div className="checkoutHeader">
            <div className="checkoutHeaderLeft">
              <button
                className="backBtn"
                onClick={() => navigate("/app/kit-cart")}
              >
                ←
              </button>
              <h1 className="checkoutTitle">Checkout</h1>
            </div>
          </div>

          <div className="checkoutContent">
            <section className="checkoutFormCard">
              <>
                <h2 className="sectionTitle mt24">Select User</h2>
                <div
                  className="usersSearchBar"
                  style={{ display: "flex", gap: 8, marginBottom: 12 }}
                >
                  <input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users by name or email"
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                    }}
                  />
                  <button
                    onClick={searchUsers}
                    disabled={isLoadingUsers}
                    className="btn btn-primary"
                    style={{ padding: "10px 14px", borderRadius: 8 }}
                  >
                    {isLoadingUsers ? "Searching..." : "Search"}
                  </button>
                  {userSearch && (
                    <button
                      onClick={() => {
                        setUserSearch("");
                        loadAllUsers();
                      }}
                      disabled={isLoadingUsers}
                      className="btn"
                      style={{ padding: "10px 14px", borderRadius: 8 }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {isLoadingUsers ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading users...</p>
                  </div>
                ) : (
                  <>
                    <div className="userCardsGrid">
                      {users.slice(0, visibleCount).map((user) => (
                        <div
                          key={user.id}
                          className={`userCard ${
                            selectedUser === user.id ? "selected" : ""
                          }`}
                          onClick={() => {
                            setSelectedUser(user.id);
                            setShowNewUserForm(false);
                            fetchPersonalInfo(user.id);
                          }}
                        >
                          <div className="userAvatar">
                            {(decryptedData(user.firstName) || "U")
                              .charAt(0)
                              .toLocaleUpperCase()}
                            {(decryptedData(user.lastName) || "S")
                              .charAt(0)
                              .toLocaleUpperCase()}
                          </div>
                          <div className="userInfo">
                            <div className="userName">
                              {decryptedData(user.firstName)}{" "}
                              {decryptedData(user.lastName)}
                            </div>
                            <div className="userEmail">
                              {decryptedData(user.email)}
                            </div>
                          </div>
                        </div>
                      ))}
                      {users.length === 0 && (
                        <div className="no-records">
                          No Associated Users Found
                        </div>
                      )}
                    </div>
                    <div className="showMoreContainer">
                      {users.length > visibleCount ? (
                        <button
                          className="showMoreBtn"
                          onClick={() => setVisibleCount((c) => c + 10)}
                          disabled={isLoadingUsers}
                        >
                          Show more
                        </button>
                      ) : (
                        users.length > 10 && (
                          <button
                            className="showLessBtn"
                            onClick={() => setVisibleCount(10)}
                            disabled={isLoadingUsers}
                          >
                            Show less
                          </button>
                        )
                      )}
                    </div>
                  </>
                )}

                <button
                  className="addNewUserBtn"
                  onClick={() => {
                    setShowNewUserForm(!showNewUserForm);
                    if (!showNewUserForm) {
                      setSelectedUser("");
                    }
                  }}
                >
                  + Add New User
                </button>

                {showNewUserForm && (
                  <div className="newUserForm">
                    <h3 className="formSubtitle">Add New User</h3>
                    <div className="formGrid">
                      <div className="formField">
                        <label>First Name</label>
                        <input
                          name="firstName"
                          value={newUser.firstName}
                          onChange={(e) => onChange(e, setNewUser)}
                          placeholder="First name"
                        />
                      </div>
                      <div className="formField">
                        <label>Last Name</label>
                        <input
                          name="lastName"
                          value={newUser.lastName}
                          onChange={(e) => onChange(e, setNewUser)}
                          placeholder="Last name"
                        />
                      </div>
                      <div className="formField colSpan2">
                        <label>Email</label>
                        <input
                          name="email"
                          value={newUser.email}
                          onChange={(e) => onChange(e, setNewUser)}
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>
                    <div className="formActions">
                      <button
                        className="saveUserBtn"
                        onClick={addNewUser}
                        disabled={isAddingUser}
                      >
                        {isAddingUser ? "Saving..." : "Save User"}
                      </button>
                      <button
                        className="cancelBtn"
                        onClick={() => {
                          if (isAddingUser) return;
                          setShowNewUserForm(false);
                          setNewUser({
                            firstName: "",
                            lastName: "",
                            email: "",
                          });
                        }}
                        disabled={isAddingUser}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {orderType === "provider" && selectedUser && (
                  <div className="shippingFormCard">
                    <h3 className="formSubtitle">Shipping Information</h3>
                    <p className="formHint">
                      Prefilled where available. Please review and complete any
                      missing fields.
                    </p>
                    <div className="formGrid">
                      <div className="formField colSpan2">
                        <label>Shipping Method</label>
                        <div className="selectWrapper">
                          <select
                            className="selectStyled"
                            value={shippingMethod}
                            onChange={(e) => setShippingMethod(e.target.value)}
                          >
                            <option value="" disabled>
                              Select Shipping Method
                            </option>
                            {shippingMethods.map((m) => (
                              <option key={m.code} value={m.code}>
                                {`${m.name} (${
                                  m.price > 0
                                    ? `$${m.price.toFixed(2)}`
                                    : "Free"
                                })`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="formField colSpan2">
                        <label>Attention To</label>
                        <input
                          name="attentionTo"
                          value={shipTo.attentionTo}
                          onChange={(e) => onChange(e, setShipTo)}
                          placeholder="Full name"
                        />
                      </div>
                      <div className="formField colSpan2">
                        <label>Address Line 1</label>
                        <input
                          name="addressLine1"
                          value={shipTo.addressLine1}
                          onChange={(e) => onChange(e, setShipTo)}
                          placeholder="Street address"
                        />
                      </div>
                      <div className="formField colSpan2">
                        <label>Address Line 2</label>
                        <input
                          name="addressLine2"
                          value={shipTo.addressLine2}
                          onChange={(e) => onChange(e, setShipTo)}
                          placeholder="Apt, suite, etc. (optional)"
                        />
                      </div>
                      <div className="formField">
                        <label>City</label>
                        <input
                          name="city"
                          value={shipTo.city}
                          onChange={(e) => onChange(e, setShipTo)}
                          placeholder="City"
                        />
                      </div>
                      <div className="formField">
                        <label>Region/State</label>
                        <input
                          name="region"
                          value={shipTo.region}
                          onChange={(e) => onChange(e, setShipTo)}
                          placeholder="State"
                        />
                      </div>
                      <div className="formField">
                        <label>Postal Code</label>
                        <input
                          name="postalCode"
                          value={shipTo.postalCode}
                          onChange={(e) => onChange(e, setShipTo)}
                          placeholder="Zip code"
                        />
                      </div>
                      <div className="formField">
                        <label>Country</label>
                        <input
                          name="countryCode"
                          value={shipTo.countryCode || "USA"}
                          readOnly
                          placeholder="Country"
                          className="readonly-field"
                        />
                      </div>
                      <div className="formField">
                        <label>Contact Phone</label>
                        <input
                          name="contactPhoneNumber"
                          value={shipTo.contactPhoneNumber}
                          onChange={(e) => onChange(e, setShipTo)}
                          placeholder="(555) 555-5555"
                        />
                      </div>
                      <div className="formField">
                        <label>Tracking Email</label>
                        <input
                          name="trackingEmail"
                          value={shipTo.trackingEmail}
                          onChange={(e) => onChange(e, setShipTo)}
                          placeholder="tracking@example.com"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            </section>

            <div className="checkoutSidebar">
              <div className="orderTypeSelector" style={{ marginBottom: 16 }}>
                <button
                  className={`orderTypeBtn ${
                    orderType === "user" ? "active" : ""
                  }`}
                  onClick={() => setOrderType("user")}
                >
                  User Pay
                </button>
                <button
                  className={`orderTypeBtn ${
                    orderType === "provider" ? "active" : ""
                  }`}
                  onClick={() => setOrderType("provider")}
                >
                  Pay Now
                </button>
              </div>
              <aside className="checkoutSummaryCard">
                <h3 className="summaryTitle">Order Summary</h3>
                {items.length > 0 && (
                  <div className="summaryItems">
                    {items.map((item, i) => (
                      <div className="summaryItem" key={`${item.code}-${i}`}>
                        <div className="summaryItemInfo">
                          <span className="summaryItemName">{item.name}</span>
                        </div>
                        <div className="summaryItemPrice">
                          ${(item.totalPrice || 0).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="summaryRow">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {shippingCost > 0 && (
                  <div className="summaryRow">
                    <span>Shipping</span>
                    <span>${shippingCost.toFixed(2)}</span>
                  </div>
                )}
                {orderType === "provider" && (
                  <div className="summaryRow">
                    <span>Provider Discount (20%)</span>
                    <span>-${(subtotalWithShipping * 0.2).toFixed(2)}</span>
                  </div>
                )}
                <div className="summaryDivider" />
                <div className="summaryRow total" style={{ marginTop: 8 }}>
                  <span>Total</span>
                  <span>${currentTotal.toFixed(2)}</span>
                </div>
                <button
                  className="placeOrderBtn"
                  onClick={placeOrder}
                  disabled={
                    orderType === "provider"
                      ? isProviderPayDisabled
                      : isUserPayDisabled
                  }
                  style={{ marginTop: 12 }}
                >
                  {orderType === "provider" ? `Pay Now` : `Add Order`}
                </button>
              </aside>

              {orderType === "user" && selectedUser && (
                <aside className="selectedUserCard">
                  <h3 className="formSubtitle">Selected User Information</h3>
                  <div className="formField" style={{ marginBottom: 16 }}>
                    <label>Shipping Method</label>
                    <div className="selectWrapper">
                      <select
                        className="selectStyled"
                        value={shippingMethod}
                        onChange={(e) => setShippingMethod(e.target.value)}
                      >
                        <option value="" disabled>
                          Select Shipping Method
                        </option>
                        {shippingMethods.map((m) => (
                          <option key={m.code} value={m.code}>
                            {`${m.name} (${
                              m.price > 0 ? `$${m.price.toFixed(2)}` : "Free"
                            })`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {isLoadingPersonal ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <p>Loading user information...</p>
                    </div>
                  ) : (
                    <div className="userInfoCard">
                      <div className="userInfoHeader">
                        <div className="userAvatar">
                          {(
                            decryptedData(
                              users.find((u) => u.id === selectedUser)
                                ?.firstName
                            ) || "U"
                          )
                            .charAt(0)
                            .toLocaleUpperCase()}
                          {(
                            decryptedData(
                              users.find((u) => u.id === selectedUser)?.lastName
                            ) || "S"
                          )
                            .charAt(0)
                            .toLocaleUpperCase()}
                        </div>
                        <div className="userBasicInfo">
                          <h4 className="userName">
                            {decryptedData(
                              users.find((u) => u.id === selectedUser)
                                ?.firstName
                            )}{" "}
                            {decryptedData(
                              users.find((u) => u.id === selectedUser)?.lastName
                            )}
                          </h4>
                          <p className="userEmail">
                            {decryptedData(
                              users.find((u) => u.id === selectedUser)?.email
                            )}
                          </p>
                        </div>
                      </div>

                      {personalInfo && (
                        <div className="userDetailedInfo">
                          <div className="infoGrid">
                            <div className="infoItem">
                              <label>Date of Birth:</label>
                              <span>
                                {decryptedData(personalInfo.dob) || "N/A"}
                              </span>
                            </div>
                            <div className="infoItem">
                              <label>Gender:</label>
                              <span>
                                {decryptedData(personalInfo.gender) || "N/A"}
                              </span>
                            </div>
                            <div className="infoItem">
                              <label>Height:</label>
                              <span>
                                {decryptedData(personalInfo.height) || "N/A"}
                              </span>
                            </div>
                            <div className="infoItem">
                              <label>Weight:</label>
                              <span>
                                {decryptedData(personalInfo.weight) || "N/A"}
                              </span>
                            </div>
                            <div className="infoItem">
                              <label>Mobile:</label>
                              <span>
                                {decryptedData(personalInfo.mobileNumber) ||
                                  "N/A"}
                              </span>
                            </div>
                            <div className="infoItem">
                              <label>Address:</label>
                              <span>
                                {decryptedData(personalInfo.address1) || "N/A"}
                                {decryptedData(personalInfo.address2) &&
                                  `, ${decryptedData(personalInfo.address2)}`}
                              </span>
                            </div>
                            <div className="infoItem">
                              <label>City:</label>
                              <span>
                                {decryptedData(personalInfo.city) || "N/A"}
                              </span>
                            </div>
                            <div className="infoItem">
                              <label>State:</label>
                              <span>
                                {decryptedData(personalInfo.state) || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </aside>
              )}
            </div>
          </div>
        </div>
      </Layout>
      {showPayModal && (
        <div className="paymentModal">
          <div className="paymentModalContent">
            <div className="paymentModalHeader">
              <h3>Complete Payment</h3>
              <button
                className="modalCloseBtn"
                onClick={() => {
                  if (!isFinalizingPayment) setShowPayModal(false);
                }}
              >
                <span>×</span>
              </button>
            </div>
            <div className="paymentModalBody">
              {isFinalizingPayment && (
                <div className="paymentBlockingOverlay">
                  <div className="spinner" />
                  <div className="overlayText">Finalizing your payment...</div>
                </div>
              )}
              <div className="paymentAmount">
                <span className="amountLabel">Total Amount</span>
                <span className="amountValue">${currentTotal.toFixed(2)}</span>
              </div>
              <div
                className="paypalContainer"
                ref={paypalRef}
                style={{ pointerEvents: isFinalizingPayment ? "none" : "auto" }}
                aria-busy={isFinalizingPayment ? "true" : "false"}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default KitCheckout;
